/**
 * Authentication Service
 * Handles login/logout API calls and session management
 */

const API_BASE = ''; // Rails handles routing, so we use relative paths

/**
 * Login with email and password
 * @param {string} email - Admin user email
 * @param {string} password - Admin user password
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const login = async (email, password) => {
    try {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCSRFToken(),
            },
            credentials: 'same-origin', // Include cookies for Devise session
            body: JSON.stringify({
                email,
                password,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || 'Login failed. Please try again.',
            };
        }

        // Store admin user info in sessionStorage (optional, for displaying user info)
        if (data.admin_user) {
            sessionStorage.setItem('adminUser', JSON.stringify(data.admin_user));
        }

        return {
            success: true,
            data: data.admin_user || data,
        };
    } catch (error) {
        console.error('Login error:', error);
        return {
            success: false,
            error: 'Network error. Please check your connection.',
        };
    }
};

/**
 * Logout current user
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const logout = async () => {
    try {
        const response = await fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCSRFToken(),
            },
            credentials: 'same-origin', // Include cookies for Devise session
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || 'Logout failed.',
            };
        }

        // Clear stored user info
        sessionStorage.removeItem('adminUser');

        return {
            success: true,
        };
    } catch (error) {
        console.error('Logout error:', error);
        // Clear sessionStorage even on error, as session may be invalid
        sessionStorage.removeItem('adminUser');
        return {
            success: false,
            error: 'Network error during logout.',
        };
    }
};

/**
 * Check if user is authenticated
 * Makes a request to verify the session is still valid
 * @returns {Promise<boolean>}
 */
export const checkAuth = async () => {
    try {
        // You can call any protected endpoint or create a dedicated /auth/check endpoint
        // For now, we'll try to get the current user from sessionStorage or make a test request
        const adminUser = sessionStorage.getItem('adminUser');
        if (!adminUser) {
            return false;
        }

        // Optionally, verify session is still valid by making a request
        // This is a placeholder - you may want to create a dedicated /auth/check endpoint
        return true;
    } catch (error) {
        console.error('Auth check error:', error);
        return false;
    }
};

/**
 * Get current admin user from sessionStorage
 * @returns {object|null}
 */
export const getCurrentUser = () => {
    try {
        const userStr = sessionStorage.getItem('adminUser');
        return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
        console.error('Error getting current user:', error);
        return null;
    }
};

/**
 * Get CSRF token from meta tag or cookie
 * Rails includes CSRF token in meta tags or cookies
 * @returns {string}
 */
const getCSRFToken = () => {
    // Try to get from meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
        return metaTag.getAttribute('content');
    }

    // Fallback: try to get from cookie
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find((cookie) => cookie.trim().startsWith('csrf-token='));
    if (csrfCookie) {
        return csrfCookie.split('=')[1];
    }

    return '';
};

